
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VisibilityRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { Dialog, Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { endpoints } from "../../api/actions/api";
import ViewPurch from "../purchase/ViewPurch";
import ReportPrintButton from "./ReportPrintButton";
import { formatCurrency } from "../../hooks/Currency";
import { formatDateTime } from "../../hooks/DateFormat";

const PurchaseReport = (props) => {
  const { t } = useTranslation();
  const { _store, _get } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [viewData, setViewData] = useState(null);

  // Date range filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load purchases
  useEffect(() => {
    setLoader(true);

    const onComplete = (res) => {
      setLoader(false);

      if (res?.status === 200 && Array.isArray(res.result)) {
        setPurchases(res.result);
      } else {
        setPurchases([]);
      }
    };

    _get(endpoints.Purchase, null, onComplete);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter purchases by selected date range
  const filteredPurchases = useMemo(() => {
    if (invalidRange) return [];

    return purchases.filter((purch) => {
      if (!fromDate && !toDate) return true;

      if (!purch.purchDate) return false;

      const purchaseDate = new Date(purch.purchDate);

      if (Number.isNaN(purchaseDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(`${fromDate}T00:00:00`);

        if (purchaseDate < from) return false;
      }

      if (toDate) {
        const to = new Date(`${toDate}T23:59:59.999`);

        if (purchaseDate > to) return false;
      }

      return true;
    });
  }, [purchases, fromDate, toDate, invalidRange]);

  // Prepare table data
  const tableData = useMemo(() => {
    return filteredPurchases.map((purch) => {
      const purchaseItems = purch.purchItems || [];

      const totalItem = purchaseItems.reduce(
        (sum, item) => sum + Number(item.qty || 0),
        0
      );

      const totalPrice = purchaseItems.reduce(
        (sum, item) =>
          sum +
          Number(item.qty || 0) *
            Number(item.purchPrice || 0),
        0
      );

      return {
        ...purch,

        purchDate: formatDateTime(purch.purchDate),

        purchBy: [
          purch.user?.firstName,
          purch.user?.lastName,
        ]
          .filter(Boolean)
          .join(" "),

        totalItem,

        totalPrice: formatCurrency(totalPrice, _store),

        supplier: purch.supplier?.company || "",
      };
    });
  }, [filteredPurchases, _store]);

  // Preserve existing working View functionality
  const handleView = (data) => {
    setViewData(data);
    setShowForm(true);
  };

  // View action - screen only
  const ActionComp = (data) => (
    <Box>
      <Tooltip arrow title={t("viewPurchase")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Screen table columns - includes Actions
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "purchDate", label: t("purchaseDate") },
    { id: "purchBy", label: t("purchaseBy") },
    { id: "totalItem", label: t("totalItem") },
    { id: "totalPrice", label: t("totalPrice") },
    { id: "supplier", label: t("supplierName") },
  ];

  // Print table columns - excludes Actions
  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  return (
    <>
      {/* Date filters and Print Preview button */}
      {/* These controls are excluded from printing */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("purchaseReport")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("purchaseReport")}
        />
      </Box>

      {/* Normal screen table - Search and Pagination enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("purchaseReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={ActionComp}
          HeaderComp={() => <div />}
          tblHead={screenColumns}
          tblData={tableData}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="purchase-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("purchaseReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* PrintMode removes Search/Pagination and shows all rows */}
        <Table
          printMode={true}
          HeaderComp={() => <div />}
          ActionComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View Purchase dialog - NOT included in printing */}
      {viewData && (
        <Dialog
          show={showForm}
          onClose={false}
          maxWidth="lg"
        >
          <ViewPurch
            setShowForm={setShowForm}
            viewData={viewData}
          />
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _get: actions.get,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(PurchaseReport);
